// Transcript repair: the repair panel with the quality report, and the
// repaired text of each transcript row. Owner: WP-8 (BUILD 1, PLAN-ADDENDUM
// A3).
//
// MeetingRepairPanel sits above the notes in a ready meeting: the repair
// state, the counts, the full edit ledger to review (Accept / Reject / Undo)
// and the per-meeting quality report (coverage windows, repeated phrases,
// uncertain spans, provenance).
//
// MeetingRepairText renders one row's text inside the transcript (WP-6's rows
// call it through the dispatcher's `renderText`). Each edited span is
// underlined: an applied correction solid, a suggestion dashed. Its popover
// shows what was heard, the correction, why, the evidence and the confidence,
// with Accept / Reject / Undo. Uncertain spans are marked, never guessed.
// Copy: ui/i18n/ns/meetings-repair.ts. Props: meeting-slots.ts.
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Popover } from 'radix-ui';
import meetingsRepairCopy from '@/i18n/ns/meetings-repair';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { BottomSheet } from '@/components/ui/sheet';
import { useNarrowViewport } from '@/lib/mobile';
import { useWorkspace } from './context';
import { meetingsApi } from './meetings-api';
import {
  clock,
  currentQuality,
  editActions,
  repairCounts,
  repairPieces,
  rowEdits,
  rowUncertain,
  type RepairPiece,
} from './meeting-repair-model';
import type { MeetingRepairPanelProps, MeetingRepairTextProps } from './meeting-slots';
import type {
  CompanyMeeting,
  MeetingQualityReport,
  MeetingRepairAction,
  MeetingRepairEvidence,
  MeetingRepairKind,
  MeetingRepairStatus,
  MeetingTranscriptEdit,
} from './types';
import './meeting-repair.css';

type RepairCopy = Readonly<Record<keyof (typeof meetingsRepairCopy)['en'], string>>;
type CopyKey = keyof RepairCopy;

const STATUS_LINE: Record<MeetingRepairStatus, CopyKey | null> = {
  none: null,
  pending: 'repairRunning',
  running: 'repairRunning',
  stale: 'repairRunning',
  current: 'repairCurrent',
  failed: 'repairFailed',
  skipped: 'repairSkipped',
};
const KIND_LABEL: Record<MeetingRepairKind, CopyKey> = {
  proper_noun: 'kindProperNoun',
  term: 'kindTerm',
  number: 'kindNumber',
  date: 'kindDate',
  negation: 'kindNegation',
  wording: 'kindWording',
  other: 'kindOther',
};
const ACTION_LABEL: Record<MeetingRepairAction, CopyKey> = {
  accept: 'accept',
  reject: 'reject',
  undo: 'undo',
};
const UNCERTAIN_LABEL: Record<string, CopyKey> = {
  engines_disagree: 'uncertainDisagree',
  low_confidence: 'uncertainLowConfidence',
  long_disagreement: 'uncertainLong',
};

const percent = (value: number) => Math.round(value * 100);

function editState(edit: MeetingTranscriptEdit, copy: RepairCopy): string {
  if (edit.status === 'applied') return edit.autoApplied ? copy.autoApplied : copy.accepted;
  if (edit.status === 'rejected') return copy.rejected;
  if (edit.status === 'undone') return copy.undone;
  return copy.suggestion;
}

function evidenceLine(item: MeetingRepairEvidence, copy: RepairCopy): string {
  const text = item.text ?? item.ref;
  if (item.kind === 'engine') return formatCopy(copy.evidenceEngine, { text });
  if (item.kind === 'vocabulary') return formatCopy(copy.evidenceVocabulary, { term: item.ref });
  if (item.kind === 'knowledge') return formatCopy(copy.evidenceKnowledge, { excerpt: text });
  return formatCopy(copy.evidenceContext, { excerpt: text });
}

const uncertainLabel = (reason: string, copy: RepairCopy) =>
  copy[UNCERTAIN_LABEL[reason] ?? 'uncertainUnchecked'];

/** Accept, reject or undo one edit; the canvas reloads the meeting afterwards. */
function useDecision(meeting: CompanyMeeting, onDone: () => void) {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(meetingsRepairCopy);
  const [busy, setBusy] = useState<string | null>(null);
  const decide = useCallback(
    async (edit: MeetingTranscriptEdit, action: MeetingRepairAction) => {
      setBusy(edit.id);
      try {
        await meetingsApi.decideEdit(meeting.id, edit.id, action);
        onDone();
      } catch {
        ws.notify(copy.actionFailed);
      } finally {
        setBusy(null);
      }
    },
    [meeting.id, onDone, ws, copy.actionFailed],
  );
  return { busy, decide };
}

function EditDetails({
  edit,
  copy,
  busy,
  onDecide,
  editable = true,
}: {
  edit: MeetingTranscriptEdit;
  copy: RepairCopy;
  busy: boolean;
  onDecide: (action: MeetingRepairAction) => void;
  editable?: boolean;
}) {
  // Spans only: the popover sits inside transcript text, where block
  // elements are not allowed; the stylesheet lays these out as blocks.
  return (
    <>
      <span className="ws-repair-change">
        <span className="ws-repair-line">
          <span className="ws-repair-key">{copy.heard}</span> <del>{edit.beforeText || '∅'}</del>
        </span>
        <span className="ws-repair-line">
          <span className="ws-repair-key">{copy.corrected}</span> <ins>{edit.afterText || '∅'}</ins>
        </span>
        {edit.reason && (
          <span className="ws-repair-line">
            <span className="ws-repair-key">{copy.reason}</span> {edit.reason}
          </span>
        )}
      </span>
      {edit.evidence.length > 0 && (
        <span className="ws-repair-evidence">
          <span className="ws-repair-key">{copy.evidence}</span>
          {edit.evidence.map((item, index) => (
            <span key={`${item.kind}-${index}`} className="ws-repair-line">
              {evidenceLine(item, copy)}
            </span>
          ))}
        </span>
      )}
      <span className="ws-repair-meta">
        {copy[KIND_LABEL[edit.kind]]} · {editState(edit, copy)} ·{' '}
        {formatCopy(copy.confidence, { percent: percent(edit.confidence) })}
      </span>
      {editable && (
        <span className="ws-repair-actions">
          {editActions(edit).map((action) => (
            <button
              key={action}
              type="button"
              className={`ws-button${action === 'accept' ? ' primary' : ''}`}
              data-repair-action={action}
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation();
                onDecide(action);
              }}
            >
              {copy[ACTION_LABEL[action]]}
            </button>
          ))}
        </span>
      )}
    </>
  );
}

// ── Inline spans in the transcript ──────────────────────────────────────────

function EditSpan({
  meeting,
  piece,
  open,
  onToggle,
  onChanged,
  editable = meeting.canEdit !== false,
}: {
  meeting: CompanyMeeting;
  piece: Extract<RepairPiece, { kind: 'edit' }>;
  open: boolean;
  onToggle: (next: boolean) => void;
  onChanged: () => void;
  editable?: boolean;
}) {
  const copy = useNamespaceCopy(meetingsRepairCopy);
  const narrow = useNarrowViewport();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const { busy, decide } = useDecision(meeting, () => {
    onToggle(false);
    onChanged();
  });
  const { edit } = piece;
  const label = edit.status === 'applied' ? copy.autoApplied : copy.suggestion;
  const name = edit.status === 'applied' && !edit.autoApplied ? copy.accepted : label;
  const details = (
    <EditDetails
      edit={edit}
      copy={copy}
      busy={busy === edit.id}
      onDecide={(action) => void decide(edit, action)}
      editable={editable}
    />
  );
  const word = piece.text || (edit.status === 'suggested' ? '+' : '·');
  const buttonProps = {
    ref: trigger,
    type: 'button' as const,
    className: `ws-repair-span ws-repair-${edit.status}`,
    'data-repair-edit': edit.id,
    title: edit.status === 'applied' ? edit.beforeText : edit.afterText,
  };
  // As before, a click on the word or in its details stays with the edit.
  const stop = (event: { stopPropagation: () => void }) => event.stopPropagation();
  // Phones get the kit's bottom sheet; elsewhere a Radix popover (DESIGN-SPEC §6.9). Both
  // close on Esc and on a press outside.
  if (narrow)
    return (
      <span className="ws-repair-anchor">
        <button
          {...buttonProps}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={(event) => {
            stop(event);
            onToggle(!open);
          }}
        >
          {word}
        </button>
        <BottomSheet
          open={open}
          onOpenChange={onToggle}
          title={name}
          returnFocus={trigger}
          contentProps={{ className: 'pc-portal' }}
        >
          <span className="ws-repair-popover" data-mode="sheet" onClick={stop}>
            {details}
          </span>
        </BottomSheet>
      </span>
    );
  return (
    <span className="ws-repair-anchor">
      <Popover.Root open={open} onOpenChange={onToggle}>
        <Popover.Trigger asChild>
          <button {...buttonProps} onClick={stop}>
            {word}
          </button>
        </Popover.Trigger>
        {/* Portalled: the transcript row is inline text, and the details read in the app's palette. */}
        <Popover.Portal>
          <Popover.Content
            ref={panel}
            tabIndex={-1}
            className="ws-repair-popover pc-portal"
            side="bottom"
            align="start"
            sideOffset={6}
            collisionPadding={12}
            aria-label={name}
            // Focus the details, not their first decision: Tab reaches Accept / Reject, and
            // Enter cannot decide by accident.
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              panel.current?.focus({ preventScroll: true });
            }}
            onClick={stop}
          >
            {details}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </span>
  );
}

export function MeetingRepairText({ meeting, segment, raw, onChanged }: MeetingRepairTextProps) {
  const copy = useNamespaceCopy(meetingsRepairCopy);
  const [open, setOpen] = useState<string | null>(null);
  if (raw) return <>{segment.rawText}</>;
  if (meeting.canEdit === false) return <>{segment.displayText}</>;
  const edits = rowEdits(meeting, segment.index);
  const uncertain = rowUncertain(meeting, segment.index);
  if (!edits.length && !uncertain.length) return <>{segment.displayText}</>;
  const pieces = repairPieces(segment, edits, uncertain);
  if (!pieces) return <>{segment.displayText}</>;
  return (
    <>
      {pieces.map((piece, index) => (
        <Fragment key={index}>
          {index ? ' ' : ''}
          {piece.kind === 'text' && piece.text}
          {piece.kind === 'uncertain' && (
            <mark
              className="ws-repair-uncertain"
              title={`${copy.uncertain}: ${uncertainLabel(piece.reason, copy)}`}
            >
              {piece.text}
            </mark>
          )}
          {piece.kind === 'edit' && (
            <EditSpan
              meeting={meeting}
              piece={piece}
              open={open === piece.edit.id}
              onToggle={(next) => setOpen(next ? piece.edit.id : null)}
              onChanged={onChanged}
            />
          )}
        </Fragment>
      ))}
    </>
  );
}

// ── The panel ───────────────────────────────────────────────────────────────

function LedgerItem({
  meeting,
  edit,
  copy,
  busy,
  jump,
  onDecide,
  editable = meeting.canEdit !== false,
}: {
  meeting: CompanyMeeting;
  edit: MeetingTranscriptEdit;
  copy: RepairCopy;
  busy: boolean;
  jump: (index: number) => void;
  onDecide: (action: MeetingRepairAction) => void;
  editable?: boolean;
}) {
  const segment = meeting.segments?.find((item) => item.index === edit.segmentIndex);
  return (
    <li className={`ws-repair-item ws-repair-${edit.status}`} data-repair-item={edit.id}>
      <EditDetails edit={edit} copy={copy} busy={busy} onDecide={onDecide} editable={editable} />
      <button type="button" className="ws-text-button" onClick={() => jump(edit.segmentIndex)}>
        {copy.showInTranscript}
        {segment?.startMs != null ? ` · ${clock(segment.startMs)}` : ''}
      </button>
    </li>
  );
}

function QualityReport({
  meeting,
  quality,
  copy,
  jump,
}: {
  meeting: CompanyMeeting;
  quality: MeetingQualityReport;
  copy: RepairCopy;
  jump: (index: number) => void;
}) {
  const provenance = meeting.provenance;
  const flagged = quality.coverage.filter((window) => window.flagged);
  return (
    <details className="ws-repair-quality">
      <summary>{copy.qualityTitle}</summary>
      {provenance?.words && provenance.speakers && (
        <p>
          {formatCopy(copy.provenance, {
            words: provenance.words.provider,
            speakers: provenance.speakers.provider,
          })}
        </p>
      )}
      {provenance?.words && !provenance.speakers && (
        <p>{formatCopy(copy.provenanceWords, { words: provenance.words.provider })}</p>
      )}
      {flagged.length ? (
        <>
          <p data-quality="coverage">
            {formatCopy(copy.coverageFlagged, { count: flagged.length })}
          </p>
          <ul>
            {flagged.map((window) => (
              <li key={window.startMs}>
                {formatCopy(copy.coverageWindow, {
                  start: clock(window.startMs),
                  end: clock(window.endMs),
                })}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p data-quality="coverage">{copy.coverageComplete}</p>
      )}
      {quality.repeatedPhrases.length > 0 && (
        <p data-quality="repeated">
          {formatCopy(copy.repeatedPhrases, { count: quality.repeatedPhrases.length })}{' '}
          {quality.repeatedPhrases.map((phrase, index) => (
            <button
              key={index}
              type="button"
              className="ws-citation"
              onClick={() => jump(phrase.spans[0]?.segmentIndex ?? 0)}
            >
              #{(phrase.spans[0]?.segmentIndex ?? 0) + 1}
            </button>
          ))}
        </p>
      )}
      {quality.uncertainSpans.length > 0 && (
        <p data-quality="uncertain">
          {formatCopy(copy.uncertainSpans, { count: quality.uncertainSpans.length })}
        </p>
      )}
      <p data-quality="disagreements">
        {formatCopy(copy.disagreements, { count: quality.disagreements.windows })}
      </p>
    </details>
  );
}

function statusLine(meeting: CompanyMeeting, copy: RepairCopy): string | null {
  if (
    meeting.repair?.status === 'failed' &&
    meeting.repair.failureCode === 'meeting_repair_second_pass_failed'
  )
    return copy.repairSkipped;
  const key = STATUS_LINE[meeting.repair?.status ?? 'none'];
  return key ? copy[key] : null;
}

function CountsLine({ meeting, copy }: { meeting: CompanyMeeting; copy: RepairCopy }) {
  const { applied, suggested } = repairCounts(meeting);
  if (meeting.repair?.status !== 'current') return null;
  if (!applied && !suggested) return <p className="ws-muted">{copy.noSuggestions}</p>;
  return (
    <p className="ws-muted" data-repair-counts>
      {[
        applied ? formatCopy(copy.appliedCount, { count: applied }) : null,
        suggested ? formatCopy(copy.suggestedCount, { count: suggested }) : null,
      ]
        .filter(Boolean)
        .join(' · ')}
    </p>
  );
}

export function MeetingRepairPanel({ meeting, jump, onChanged }: MeetingRepairPanelProps) {
  const copy = useNamespaceCopy(meetingsRepairCopy);
  const ws = useWorkspace();
  const [open, setOpen] = useState(false);
  const [ledger, setLedger] = useState<MeetingTranscriptEdit[] | null>(null);
  const [decided, setDecided] = useState(false);
  const load = useCallback(() => {
    void meetingsApi
      .edits(meeting.id)
      .then((result) => setLedger(result.edits))
      .catch(() => ws.notify(copy.actionFailed));
  }, [meeting.id, ws, copy.actionFailed]);
  useEffect(() => {
    if (open) load();
  }, [open, load, meeting.updatedAt]);
  const { busy, decide } = useDecision(meeting, () => {
    setDecided(true);
    onChanged();
    load();
  });
  const quality = currentQuality(meeting);
  const status = statusLine(meeting, copy);
  const notesRefreshing =
    decided && (meeting.notes?.status === 'stale' || meeting.notes?.status === 'generating');
  if (!status && !quality) return null;
  // Rejected and undone edits leave meeting.edits; the report still counts them.
  const reported =
    (quality?.disagreements.suggested ?? 0) + (quality?.disagreements.autoApplied ?? 0);
  const hasEdits = (meeting.edits?.length ?? 0) > 0 || reported > 0 || (ledger?.length ?? 0) > 0;
  return (
    <section className="ws-repair-panel" aria-label={copy.repairTitle}>
      <header>
        <h2>{copy.repairTitle}</h2>
        {status && (
          <p role="status" data-repair-status={meeting.repair.status}>
            {status}
          </p>
        )}
      </header>
      <CountsLine meeting={meeting} copy={copy} />
      {notesRefreshing && <p className="ws-muted">{copy.notesRegenerating}</p>}
      {hasEdits && meeting.canEdit !== false && (
        <button
          type="button"
          className="ws-text-button"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? copy.hideSuggestions : copy.reviewSuggestions}
        </button>
      )}
      {open && ledger && (
        <ul className="ws-repair-list">
          {ledger.map((edit) => (
            <LedgerItem
              key={edit.id}
              meeting={meeting}
              edit={edit}
              copy={copy}
              busy={busy === edit.id}
              jump={jump}
              onDecide={(action) => void decide(edit, action)}
              editable={meeting.canEdit !== false}
            />
          ))}
        </ul>
      )}
      {quality && <QualityReport meeting={meeting} quality={quality} copy={copy} jump={jump} />}
      <p className="ws-muted ws-repair-safe">{copy.safeChangesOnly}</p>
    </section>
  );
}
